<template>
  <Modal ref="modal" class="send-approval-modal">
    <template v-if="!created">
      <h2 class="box__title">{{ $t('requestRating.title') }}</h2>
      <p class="send-approval__intro">{{ $t('requestRating.intro') }}</p>

      <FormGroup :label="$t('requestRating.what')" required>
        <input v-model="title" class="input" maxlength="255" />
      </FormGroup>

      <FormGroup :label="$t('createChecklist.message')">
        <textarea
          v-model="message"
          class="input send-approval__textarea"
          rows="2"
          :placeholder="$t('requestRating.messagePlaceholder')"
          maxlength="4000"
        ></textarea>
      </FormGroup>

      <FormGroup :label="$t('sendApproval.contact')">
        <div v-if="recentContacts.length" class="send-approval__recent">
          <a
            v-for="(contact, index) in recentContacts"
            :key="index"
            class="send-approval__recent-chip"
            @click="applyContact(contact)"
          >
            {{ contact.name || contact.phone || contact.email }}
          </a>
        </div>
        <div class="send-approval__contact-grid">
          <input
            v-model="contactName"
            class="input"
            :placeholder="$t('sendApproval.contactName')"
            maxlength="255"
          />
          <input
            v-model="contactPhone"
            class="input"
            :placeholder="$t('sendApproval.contactPhone')"
            maxlength="64"
          />
          <input
            v-model="contactEmail"
            class="input"
            :placeholder="$t('sendApproval.contactEmail')"
            maxlength="255"
          />
        </div>
      </FormGroup>

      <div class="actions actions--right">
        <Button
          type="primary"
          size="large"
          :loading="saving"
          :disabled="!title.trim() || saving"
          @click="submit"
        >
          {{ $t('requestRating.create') }}
        </Button>
      </div>
    </template>
    <template v-else>
      <h2 class="box__title">{{ $t('sendApproval.readyTitle') }}</h2>
      <p class="send-approval__ready-text">{{ $t('requestRating.readyText') }}</p>
      <div class="send-approval__link">
        <input
          class="input"
          :value="created.public_url"
          readonly
          @focus="$event.target.select()"
        />
        <Button type="secondary" icon="iconoir-copy" @click="copyLink">
          {{ $t('sendApproval.copy') }}
        </Button>
      </div>
      <div class="send-approval__channels">
        <Button type="primary" icon="iconoir-chat-bubble" @click="openWhatsApp">
          WhatsApp
        </Button>
        <Button
          type="secondary"
          icon="iconoir-send-mail"
          :loading="sendingEmail"
          :disabled="!contactEmail || sendingEmail"
          @click="sendEmail"
        >
          {{ $t('sendApproval.sendEmail') }}
        </Button>
        <Button type="secondary" @click="hide()">
          {{ $t('sendApproval.done') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import { notifyIf } from '@baserow/modules/core/utils/error'
import ApprovalsService from '@baserow/modules/core/services/approvals'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import { buildRatingShareMessage } from '@baserow/modules/database/utils/approvalShare'

export default {
  name: 'RequestRatingModal',
  mixins: [modal],
  props: {
    row: { type: Object, required: true },
    table: { type: Object, required: true },
    fields: { type: Array, required: true },
  },
  emits: ['created'],
  data() {
    return {
      title: '',
      message: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      recentContacts: [],
      saving: false,
      sendingEmail: false,
      created: null,
    }
  },
  computed: {
    service() {
      return ApprovalsService(this.$client)
    },
  },
  methods: {
    show(...args) {
      this.reset()
      this.fetchContacts()
      return modal.methods.show.call(this, ...args)
    },
    reset() {
      const primary = this.fields.find((f) => f.primary)
      this.title = primary ? String(this.row[`field_${primary.id}`] || '') : ''
      this.message = ''
      this.contactName = ''
      this.contactPhone = ''
      this.contactEmail = ''
      this.saving = false
      this.created = null
    },
    async fetchContacts() {
      try {
        const { data } = await this.service.contacts(this.table.id)
        this.recentContacts = data.contacts
      } catch (e) {
        this.recentContacts = []
      }
    },
    applyContact(contact) {
      this.contactName = contact.name || this.contactName
      this.contactPhone = contact.phone || this.contactPhone
      this.contactEmail = contact.email || this.contactEmail
    },
    async submit() {
      this.saving = true
      try {
        const { data } = await this.service.createRating(
          this.table.id,
          this.row.id,
          {
            title: this.title,
            message: this.message,
            contact_name: this.contactName,
            contact_phone: this.contactPhone,
            contact_email: this.contactEmail,
          }
        )
        this.created = data.rating
        this.$emit('created', data.rating)
      } catch (error) {
        notifyIf(error, 'requestRating')
      }
      this.saving = false
    },
    copyLink() {
      copyToClipboard(this.created.public_url)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openWhatsApp() {
      const message = buildRatingShareMessage(this.created)
      const digits = (this.created.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async sendEmail() {
      this.sendingEmail = true
      try {
        await this.service.sendRatingEmail(this.created.id, this.contactEmail)
        this.$store.dispatch('toast/success', {
          title: this.$t('sendApproval.emailSentTitle'),
          message: this.$t('sendApproval.emailSent', { to: this.contactEmail }),
        })
      } catch (error) {
        notifyIf(error, 'requestRating')
      }
      this.sendingEmail = false
    },
  },
}
</script>
